/**
 * `WebSocketWireClient` — real WebSocket transport for the `WireClient`
 * interface. Uses MessagePack over binary frames per WIRE_PROTOCOL §2.
 *
 * Behaviour summary:
 *   1. `connect(handshake)` opens `ws://…/ws?token=<devToken>`, sends
 *      `ClientMessage::Handshake`, waits for `ServerMessage::HandshakeAck`.
 *      The returned promise resolves after the ack lands.
 *   2. On subsequent server messages: `Ping` → reply `Pong` same-tick;
 *      everything else fans out to `onMessage` handlers.
 *   3. On close: classify the code (see `backoff.ts`). Retriable close →
 *      schedule reconnect with `reconnectDelayMs(attempt)`; non-retriable
 *      → emit an `error` lifecycle event and stop.
 *   4. On reconnect success the `attempt` counter resets to 0 and the
 *      pending pre-reconnect promise (if any) resolves.
 *   5. `send(intent)` encodes and ships immediately if the socket is open;
 *      throws if the caller invokes it during `reconnecting`/`disconnected`.
 *      D16's advisory throttle is the caller's job — this layer is the raw
 *      transport.
 *
 * Ping-watchdog: WIRE_PROTOCOL §10.1 says the server pings every 30 s and
 * closes after 40 s of silence with 4408. The client mirrors that timeout
 * locally — if no `Ping` arrives within `PING_WATCHDOG_MS`, the client
 * fabricates a 4408 close and triggers reconnect. This keeps a silently
 * half-open socket from looking "active" forever.
 */

import { decode, encode } from '@msgpack/msgpack';

import { phase0, debugLog } from '../log';
import { reconnectDelayMs, shouldRetry } from './backoff';
import type {
  ClientHandshake,
  ClientIntent,
  ClientMessage,
  ServerMessage,
} from './envelopes';
import type {
  ConnectionEvent,
  ConnectionEventHandler,
  IncomingHandler,
  WireClient,
} from './client';

const PING_WATCHDOG_MS = 45_000; // 30 s ping cadence + 15 s slack.
const PING_TIMEOUT_CODE = 4408;

export interface WebSocketWireClientOptions {
  /** Base WebSocket URL, e.g. `ws://localhost:4000/ws`. Token is appended. */
  url: string;
  /** Dev token that identifies the player to the Gateway. */
  token: string;
  /** Test seam: injected WebSocket constructor. Defaults to `globalThis.WebSocket`. */
  WebSocketCtor?: typeof WebSocket;
  /** Test seam: injected timer primitives. Defaults to `globalThis.setTimeout`. */
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
}

type InternalStage =
  | 'idle' // pre-connect
  | 'opening' // WS opening
  | 'awaiting_ack' // WS open, handshake sent, awaiting HandshakeAck
  | 'active' // ack received
  | 'reconnecting' // scheduled a retry
  | 'closed'; // terminal — connect() rejected or user called close()

export class WebSocketWireClient implements WireClient {
  private readonly opts: Required<
    Omit<WebSocketWireClientOptions, 'setTimeoutFn' | 'clearTimeoutFn'>
  > & {
    setTimeoutFn: typeof setTimeout;
    clearTimeoutFn: typeof clearTimeout;
  };
  private readonly messageHandlers = new Set<IncomingHandler>();
  private readonly eventHandlers = new Set<ConnectionEventHandler>();

  private socket: WebSocket | null = null;
  private stage: InternalStage = 'idle';
  private attempt = 0;
  private handshake: ClientHandshake | null = null;
  private pendingConnect: {
    resolve: () => void;
    reject: (err: Error) => void;
  } | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingWatchdog: ReturnType<typeof setTimeout> | null = null;

  constructor(options: WebSocketWireClientOptions) {
    const defaultCtor =
      typeof globalThis !== 'undefined' ? globalThis.WebSocket : undefined;
    if (!options.WebSocketCtor && !defaultCtor) {
      throw new Error(
        'WebSocketWireClient: no WebSocket constructor available (pass `WebSocketCtor` in environments without `globalThis.WebSocket`)',
      );
    }
    this.opts = {
      url: options.url,
      token: options.token,
      WebSocketCtor: options.WebSocketCtor ?? (defaultCtor as typeof WebSocket),
      // Bind to globalThis so the defaults tolerate being invoked via an
      // object-property receiver (`this.opts.setTimeoutFn(...)`). Firefox
      // enforces the WebIDL rule that setTimeout / clearTimeout's `this`
      // must be a Window; a bare reference stored on `opts` fails that
      // check. See `default timer seams — Firefox Window receiver compat`.
      setTimeoutFn:
        options.setTimeoutFn ?? (setTimeout.bind(globalThis) as typeof setTimeout),
      clearTimeoutFn:
        options.clearTimeoutFn ?? (clearTimeout.bind(globalThis) as typeof clearTimeout),
    };
  }

  // ─── WireClient surface ──────────────────────────────────────────────

  connect(handshake: ClientHandshake): Promise<void> {
    if (this.stage !== 'idle' && this.stage !== 'reconnecting') {
      throw new Error(
        `WebSocketWireClient: connect() called while stage=${this.stage}`,
      );
    }
    this.handshake = handshake;
    return new Promise<void>((resolve, reject) => {
      this.pendingConnect = { resolve, reject };
      this.openSocket();
    });
  }

  send(intent: ClientIntent): void {
    if (this.stage !== 'active' || !this.socket) {
      throw new Error(
        `WebSocketWireClient: send() while stage=${this.stage} — caller must wait for 'active' before dispatching intents`,
      );
    }
    const msg: ClientMessage = { type: 'Intent', payload: intent };
    this.socket.send(encode(msg, { useBigInt64: true }));
  }

  close(code = 1000, reason = 'client-initiated'): void {
    this.clearReconnectTimer();
    this.clearPingWatchdog();
    this.pendingConnect?.reject(
      new Error(`close() during connect (code=${code})`),
    );
    this.pendingConnect = null;
    const was = this.stage;
    this.stage = 'closed';
    if (this.socket) {
      try {
        this.socket.close(code, reason);
      } catch {
        // WebSocket.close() throws on invalid codes; swallow — we're
        // tearing down anyway.
      }
      this.socket = null;
    }
    if (was !== 'closed') {
      this.emitEvent({ kind: 'closed', code, reason });
    }
  }

  onMessage(handler: IncomingHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  onEvent(handler: ConnectionEventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => {
      this.eventHandlers.delete(handler);
    };
  }

  // ─── internal ────────────────────────────────────────────────────────

  private openSocket(): void {
    if (!this.handshake) {
      throw new Error('WebSocketWireClient: openSocket with no handshake');
    }
    this.stage = 'opening';
    const url = this.buildUrl();
    const Ctor = this.opts.WebSocketCtor;
    const ws = new Ctor(url);
    ws.binaryType = 'arraybuffer';
    this.socket = ws;

    ws.addEventListener('open', () => this.onSocketOpen());
    ws.addEventListener(
      'message',
      (ev: MessageEvent<ArrayBuffer | Blob | string>) => this.onSocketMessage(ev),
    );
    ws.addEventListener('close', (ev: CloseEvent) =>
      this.onSocketClose(ev.code, ev.reason),
    );
    ws.addEventListener('error', () => {
      // Browsers do not expose error details on WebSocket 'error' events
      // (by design). Close will follow; `onSocketClose` is the recovery path.
      this.emitEvent({
        kind: 'error',
        reason: 'transport error (close will follow)',
      });
    });
  }

  private buildUrl(): string {
    const sep = this.opts.url.includes('?') ? '&' : '?';
    return `${this.opts.url}${sep}token=${encodeURIComponent(this.opts.token)}`;
  }

  private onSocketOpen(): void {
    if (!this.socket || !this.handshake) return;
    this.emitEvent({ kind: 'opened' });
    phase0.info('wire_open', { url: this.buildUrl(), attempt: this.attempt });
    debugLog('wire', 'info', `connected url=${this.buildUrl()} attempt=${this.attempt}`);
    const msg: ClientMessage = { type: 'Handshake', payload: this.handshake };
    this.socket.send(encode(msg, { useBigInt64: true }));
    this.stage = 'awaiting_ack';
    this.emitEvent({ kind: 'handshake_sent' });
  }

  private onSocketMessage(ev: MessageEvent<ArrayBuffer | Blob | string>): void {
    const data = ev.data;
    if (!(data instanceof ArrayBuffer)) {
      // The server should always send binary msgpack frames. Non-binary
      // frames are a protocol violation; drop + record.
      this.emitEvent({
        kind: 'error',
        reason: `non-binary WebSocket frame ignored (typeof=${typeof data})`,
      });
      return;
    }
    let msg: ServerMessage;
    try {
      msg = decode(new Uint8Array(data), { useBigInt64: true }) as ServerMessage;
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      this.emitEvent({
        kind: 'error',
        reason: `msgpack decode failed: ${reason}`,
      });
      return;
    }

    this.resetPingWatchdog();

    if (msg.type === 'HandshakeAck') {
      this.stage = 'active';
      this.attempt = 0;
      this.pendingConnect?.resolve();
      this.pendingConnect = null;
      this.emitEvent({ kind: 'handshake_ack' });
      phase0.info('handshake_ack', {
        player_id: msg.payload.player_id,
        world_id: msg.payload.world_id,
        tick: msg.payload.tick,
        tick_hz: msg.payload.tick_hz,
        reconnect_mode: msg.payload.reconnect_mode.kind,
      });
      debugLog('wire', 'info', `handshake_ack player=${msg.payload.player_id} world=${msg.payload.world_id} tick=${msg.payload.tick}`);
      // Fan out — callers read `reconnect_mode` from the payload to
      // branch on Fresh/Replay/Full.
      for (const h of this.messageHandlers) h(msg);
      this.emitEvent({ kind: 'active' });
      return;
    }

    if (msg.type === 'Ping' && this.socket) {
      const pong: ClientMessage = {
        type: 'Pong',
        payload: { nonce: msg.payload.nonce },
      };
      this.socket.send(encode(pong, { useBigInt64: true }));
    }

    // Route Snapshot + Event + Error + Ping through the normal fan-out.
    for (const h of this.messageHandlers) h(msg);

    if (msg.type === 'Snapshot') {
      this.emitEvent({ kind: 'snapshot_applied' });
    }
  }

  private onSocketClose(code: number, reason: string): void {
    this.clearPingWatchdog();
    const prev = this.stage;
    this.socket = null;
    this.emitEvent({ kind: 'closed', code, reason });
    phase0.info('wire_close', { code, reason, prev_stage: prev });
    debugLog('wire', 'info', `disconnected code=${code} reason=${reason} prev=${prev}`);

    if (prev === 'closed') return; // user-initiated close — done

    if (!shouldRetry(code)) {
      this.stage = 'closed';
      this.pendingConnect?.reject(
        new Error(`connection closed with non-retriable code ${code}: ${reason}`),
      );
      this.pendingConnect = null;
      return;
    }

    // Retriable close → schedule reconnect.
    this.attempt += 1;
    this.stage = 'reconnecting';
    const delay = reconnectDelayMs(this.attempt);
    phase0.info('wire_reconnect_scheduled', {
      attempt: this.attempt,
      delay_ms: delay,
      code,
    });
    debugLog('wire', 'info', `reconnect scheduled attempt=${this.attempt} delay=${delay}ms`);
    this.reconnectTimer = this.opts.setTimeoutFn(() => {
      this.reconnectTimer = null;
      if (this.stage !== 'reconnecting' || !this.handshake) return;
      this.openSocket();
    }, delay);
  }

  private resetPingWatchdog(): void {
    this.clearPingWatchdog();
    this.pingWatchdog = this.opts.setTimeoutFn(() => {
      this.pingWatchdog = null;
      if (!this.socket) return;
      // Simulate a 4408 close locally; the real server may or may not
      // actually have sent one, but the watchdog semantics align.
      try {
        this.socket.close(PING_TIMEOUT_CODE, 'client ping-watchdog timeout');
      } catch {
        // fall through to onSocketClose via the close event
      }
    }, PING_WATCHDOG_MS);
  }

  private clearPingWatchdog(): void {
    if (this.pingWatchdog !== null) {
      this.opts.clearTimeoutFn(this.pingWatchdog);
      this.pingWatchdog = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      this.opts.clearTimeoutFn(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private emitEvent(ev: ConnectionEvent): void {
    for (const h of this.eventHandlers) h(ev);
  }
}
